/**
 * NPC `deployables[]` — the nested stat block a feature deploys onto the board.
 *
 * **Promoted out of the Phase 4 tail (2026-09-06).** 13 of the shipped v3 pack's 401 features have
 * their ENTIRE content here: no `effect`, no `description`, no `actions[]`, no `active_effects[]`,
 * no weapon block. `npcf_hardlight_cover_system_aegis`, `npcf_deployable_turret_engineer`,
 * `npcf_razor_swarms_hive` and ten more rendered as completely empty record sheets, and no amount
 * of Phase 3 weapon work would have fixed them — the feature IS its deployable. Together with the
 * weapon block this is the second and last thing standing between the project and its stated goal
 * that no NPC record renders an empty body.
 *
 * A deployable is a small combatant in its own right, so it carries the same field families the
 * parent does — a stat line, a weapon block, prose — and reuses the same tier-resolving readers
 * rather than re-deriving them. Its own `add_status` / `add_resist` / `add_other` / `active_effects`
 * siblings stay owed to Phase 4; every record carrying them also carries a `detail` that states the
 * same rule in prose, so nothing is silently dropped by rendering prose plus stats here.
 */
import { statAtTier, type NpcTier } from "../../lib/npcTierText";
import { npcWeaponView, type NpcWeaponCtx, type NpcWeaponView } from "./npcWeapon";

/** One labelled stat on a deployable's line ("HP 20", "SIZE 2"). */
export type NpcDeployableStat = { label: string; value: string };

/** One deployable, tier-resolved and ready to render. */
export type NpcDeployableView = {
  name?: string;
  kind?: string;
  activation?: string;
  frequency?: string;
  redeploy?: string;
  detailHtml?: string;
  stats: NpcDeployableStat[];
  weapon?: NpcWeaponView;
};

/**
 * The stat line, in a fixed reading order rather than the object's key order, so every deployable's
 * line scans the same way. `size_special` ("Line 3") replaces a numeric SIZE when present — it is
 * the honest value for a wall, which has an extent rather than a footprint.
 */
const STAT_ORDER: readonly (readonly [string, string])[] = [
  ["hp", "HP"],
  ["armor", "ARMOR"],
  ["evasion", "EVADE"],
  ["edef", "E-DEF"],
  ["size", "SIZE"],
  ["heatcap", "HEAT"],
  ["speed", "SPEED"],
  ["save", "SAVE"],
];

const asRecord = (v: unknown): Record<string, unknown> | undefined =>
  v !== null && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined;

const asString = (v: unknown): string | undefined => (typeof v === "string" && v !== "" ? v : undefined);

function statLine(d: Record<string, unknown>, tier: NpcTier): NpcDeployableStat[] {
  const out: NpcDeployableStat[] = [];
  for (const [key, label] of STAT_ORDER) {
    if (key === "size") {
      // A `size_special` is prose ("Line 3") and outranks a numeric size when both are present.
      const special = asString(d.size_special);
      if (special !== undefined) {
        out.push({ label, value: special });
        continue;
      }
    }
    // `save` is an object here (`{type, aoe}`), not a number — it belongs to the weapon reader's
    // clause handling, not the stat line, so skip it rather than printing "[object Object]".
    if (key === "save" && asRecord(d.save) !== undefined) continue;
    const at = statAtTier(d[key], tier);
    if (at !== undefined) out.push({ label, value: String(at) });
  }
  return out;
}

/**
 * Build a feature's deployable views at `ctx.tier`, or `undefined` when it deploys nothing.
 * `ctx` is the same bag {@link npcWeaponView} takes, because a deployable's own attack is read by
 * exactly that function — a turret's `damage`/`range`/`attack_bonus` mean what they mean on the
 * parent.
 */
export function npcDeployableViews(raw: unknown, ctx: NpcWeaponCtx): NpcDeployableView[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: NpcDeployableView[] = [];
  for (const entry of raw) {
    const d = asRecord(entry);
    if (!d) continue;
    const view: NpcDeployableView = {
      name: asString(d.name),
      kind: asString(d.type),
      activation: asString(d.activation),
      frequency: asString(d.frequency),
      redeploy: asString(d.redeploy),
      detailHtml: ctx.text(asString(d.detail)) || undefined,
      stats: statLine(d, ctx.tier),
      weapon: npcWeaponView(d, ctx),
    };
    // Skip an entry that would render as an empty box; keep anything with a name, prose, or numbers.
    if (view.name || view.detailHtml || view.stats.length > 0 || view.weapon) out.push(view);
  }
  return out.length > 0 ? out : undefined;
}
